import React from 'react';
import { AppBreadcrumb } from '@components/layout';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_PREFIX_PATH } from '@src/constants/constant';
import { Tabs } from 'antd';
import utils from '@src/utils/utils';

interface PageHeaderProps {
  title?: string;
  display?: boolean;
}

export const PageHeader = ({ title, display }: PageHeaderProps) => {
  const isMobile = utils.isMobile();
  const location = useLocation();

  if (isFeatureHeaderPath(location.pathname)) {
    return <FeatureHeader />;
  }

  return isMobile ? (
    <MobileAnalysisHeader />
  ) : display ? (
    <AnalysisHeader title={title} />
  ) : null;
};
const AnalysisHeader = ({ title }: { title?: string }) => {
  const pathname = window.location.pathname;

  const isHide = pathname.includes('/analysis/main') ? true : false;
  return (
    <div className={`app-page-header`}>
      <div className="mb-0 mr-3 text">{title ? title : 'home'}</div>
      {isHide === false && <AppBreadcrumb />}
    </div>
  );
};

const featureTabs = [
  { label: '실시간 감지', path: '/app/realtime' },
  { label: '인사이트', path: '/app/ai-insight' },
  { label: '종합리포트', path: '/app/ai-report' },
  { label: '이슈리스트', path: '/app/issue-table' },
];

const isFeatureHeaderPath = (pathname: string) =>
  featureTabs.some((item) => pathname.startsWith(item.path));

const getActiveFeaturePath = (pathname: string) =>
  featureTabs.find((item) => pathname.startsWith(item.path))?.path ??
  '/app/realtime';

const FeatureHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = utils.isMobile();
  const activePath = getActiveFeaturePath(location.pathname);

  const navigateTo = (path: string) => {
    navigate(`${path}${location.search}`);
  };

  return (
    <div
      className={isMobile ? 'app-page-m-header' : 'app-page-header'}
      style={
        isMobile
          ? {
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
            }
          : undefined
      }
    >
      <nav
        style={{
          display: 'inline-flex',
          gap: 2,
          alignItems: 'center',
          backgroundColor: '#F1F3F5',
          borderRadius: 10,
          padding: 4,
        }}
      >
        {featureTabs.map((tab) => {
          const isActive = activePath === tab.path;
          return (
            <FeatureTabButton
              key={tab.path}
              label={tab.label}
              isActive={isActive}
              isMobile={isMobile}
              onClick={() => navigateTo(tab.path)}
            />
          );
        })}
      </nav>
    </div>
  );
};

const FeatureTabButton = ({
  label,
  isActive,
  isMobile,
  onClick,
}: {
  label: string;
  isActive: boolean;
  isMobile: boolean;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const baseColor = isActive
    ? '#2F80ED'
    : isHovered
      ? '#1F2937'
      : '#6B7280';
  const baseBackground = isActive
    ? '#FFFFFF'
    : isHovered
      ? 'rgba(0, 0, 0, 0.04)'
      : 'transparent';

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-current={isActive ? 'page' : undefined}
      style={{
        padding: isMobile ? '6px 12px' : '8px 16px',
        fontSize: 14,
        fontWeight: isActive ? 700 : 600,
        letterSpacing: '-0.01em',
        color: baseColor,
        backgroundColor: baseBackground,
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        boxShadow: isActive
          ? '0 1px 2px rgba(15, 23, 42, 0.08), 0 0 0 0.5px rgba(15, 23, 42, 0.04)'
          : 'none',
        transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
};

const headerLabels = [
  { label: '종합분석', path: '/analysis/main' },
  { label: '성장 추이', path: '/analysis/growth-trend' },
  { label: '키워드 검색량', path: '/analysis/naver-keyword-search-volume' },
  // { label: '홈페이지', path: '/analysis/homepage' },
  { label: '인스타그램', path: '/analysis/sns-channel/instargram' },
  { label: '블로그', path: '/analysis/sns-channel/blog' },
  { label: '스마트 스토어 분석', path: '/analysis/smart-store' },
  { label: '스마트 플레이스 분석', path: '/analysis/smart-place' },
];
const MobileAnalysisHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    return location.pathname.includes(APP_PREFIX_PATH + path);
  };

  const handleOnChangeTabs = (activeKey: string) => {
    if (!activeKey) {
      return;
    }
    navigate(activeKey);
  };

  const handleOnTabClick = (
    activeKey: string,
    e: React.KeyboardEvent | React.MouseEvent,
  ) => {
    console.log(e);
    const element: HTMLDivElement = e.target as HTMLDivElement;
    const parentElement = element?.offsetParent as HTMLDivElement;
    const scrollWrap = parentElement?.offsetParent as HTMLDivElement;

    const screenWidth = window.screen.width;
    const scrollWrapClientRect = scrollWrap?.getBoundingClientRect();
    const parentElementRect = parentElement?.getBoundingClientRect();
    const marginRight = 12;
    const spareWidth = 50;
    let moveX =
      parentElementRect.right -
      scrollWrapClientRect.x -
      screenWidth +
      marginRight +
      spareWidth;

    if (moveX > 0) {
      const maxSize = scrollWrapClientRect.width - screenWidth;

      if (moveX > maxSize) {
        moveX = maxSize;
      }
    } else {
      moveX = 0;
    }

    scrollWrap.style.transform = `translate(-${moveX}px, 0px)`;
  };

  if (isActive('/analysis') === false) {
    return null;
  }

  return (
    <div className={`app-page-m-header`}>
      <Tabs
        defaultActiveKey={window.location.pathname}
        tabPosition={'top'}
        hideAdd={true}
        size={'small'}
        items={[]}
        onChange={handleOnChangeTabs}
        onTabClick={handleOnTabClick}
      />
    </div>
  );
};
